import { useMemo, useState, type ComponentType, type ReactNode } from "react";
import {
  Box,
  Chip,
  TextField,
  Typography,
  Stack,
  InputAdornment,
  Tooltip,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import type { UserSummary } from "../types/user";
import { formatLastSeen } from "../utils/users";
import * as FlagIcons from "country-flag-icons/react/3x2";
import SearchIcon from "@mui/icons-material/Search";

type UserTableProps = {
  users: UserSummary[];
  selectedUserIds: string[];
  onSelectionChange: (ids: string[]) => void;
  toolbarActions?: ReactNode;
};

const UserTable = ({
  users,
  selectedUserIds,
  onSelectionChange,
  toolbarActions
}: UserTableProps) => {
  const [searchText, setSearchText] = useState("");
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isCompactCountry = useMediaQuery(theme.breakpoints.down("lg"));
  const regionNames = useMemo(
    () =>
      typeof Intl !== "undefined" && typeof Intl.DisplayNames !== "undefined"
        ? new Intl.DisplayNames(["en"], { type: "region" })
        : null,
    []
  );

  const normalizedSearch = useMemo(() => searchText.trim().toLowerCase(), [searchText]);

  const rows = useMemo(() => {
    if (!normalizedSearch) {
      return users;
    }

    return users.filter((user) => {
      const values = [
        user.username,
        user.name,
        user.surname,
        user.email,
        user.company,
        user.role,
        user.country
      ];
      return values.some((value) => typeof value === "string" && value.toLowerCase().includes(normalizedSearch));
    });
  }, [normalizedSearch, users]);
  const selectionModel = useMemo(
    () => ({ type: "include", ids: new Set(selectedUserIds) } as const),
    [selectedUserIds]
  );

  const columns = useMemo<GridColDef<UserSummary>[]>(() => {
    const usernameMinWidth = isSmallScreen ? 80 : 120;
    const fullNameMinWidth = isSmallScreen ? 100 : 140;
    const roleMinWidth = isSmallScreen ? 40 : 80;
    const lastSeenMinWidth = isSmallScreen ? 120 : 190;
    const countryMinWidth = isCompactCountry ? 0 : 128;
    const countryWidth = isCompactCountry ? 64 : undefined;
    const countryFlex = isCompactCountry ? 0 : 0.9;
    const buildFullName = (row?: UserSummary) => {
      if (!row) {
        return null;
      }
      const name = row.name?.trim();
      const surname = row.surname?.trim();
      if (!name && !surname) {
        return null;
      }
      return [name, surname].filter(Boolean).join(" ");
    };

    const allColumns: GridColDef<UserSummary>[] = [
      {
        field: "username",
        headerName: "User",
        flex: 0.8,
        minWidth: usernameMinWidth,
        resizable: true,
        renderCell: (params) => {
          const { username } = params.row;
          return (
            <Typography variant="body2" fontWeight={600}>
              {username}
            </Typography>
          );
        }
      },
      {
        field: "fullName",
        headerName: "Name",
        flex: 1,
        minWidth: fullNameMinWidth,
        resizable: true,
        valueGetter: (params: { row?: UserSummary }) => buildFullName(params?.row),
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {buildFullName(params.row) || "—"}
          </Typography>
        )
      },
      {
        field: "company",
        headerName: "Company",
        flex: 1,
        minWidth: 100,
        resizable: true,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {params.row.company || "—"}
          </Typography>
        )
      },
      {
        field: "country",
        headerName: "Country",
        flex: countryFlex,
        width: countryWidth,
        minWidth: countryMinWidth,
        resizable: true,
        renderCell: (params) => {
          const countryCode = params.row.country?.trim().toUpperCase();
          if (!countryCode) {
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            );
          }
          const Flag = (FlagIcons as Record<string, ComponentType<{ width?: number }> | undefined>)[countryCode];
          const name = regionNames?.of(countryCode) ?? countryCode;
          if (isCompactCountry) {
            if (Flag) {
              return (
                <Tooltip title={name}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Flag width={24} />
                  </Box>
                </Tooltip>
              );
            }
            return (
              <Tooltip title={name}>
                <Typography variant="body2">{countryCode}</Typography>
              </Tooltip>
            );
          }
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              {Flag ? <Flag width={24} /> : null}
              <Typography variant="body2">{name}</Typography>
            </Stack>
          );
        }
      },
      {
        field: "role",
        headerName: "Role",
        flex: 0.5,
        minWidth: roleMinWidth,
        sortable: true,
        resizable: true,
        renderCell: (params) => (
          <Typography variant="body2">{params.row.role}</Typography>
        )
      },
      {
        field: "active",
        headerName: "Status",
        flex: 0.5,
        minWidth: 90,
        sortable: true,
        resizable: true,
        renderCell: (params) => (
          <Chip
            label={params.row.active ? "Active" : "Disabled"}
            color={params.row.active ? "success" : "default"}
            size="small"
            variant="outlined"
          />
        )
      },
      {
        field: "lastSeen",
        headerName: "Last seen",
        flex: 1,
        minWidth: lastSeenMinWidth,
        valueGetter: (params: { row: UserSummary }) => params?.row?.lastSeen ?? null,
        resizable: true,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {formatLastSeen(params?.row?.lastSeen ?? null)}
          </Typography>
        )
      }
    ];

    if (isSmallScreen) {
      const allowedFields = new Set(["username", "country", "lastSeen"]);
      return allColumns.filter((column) => allowedFields.has(column.field));
    }

    return allColumns;
  }, [isSmallScreen, isCompactCountry, regionNames]);

  return (
    <Box sx={{ width: "100%", minWidth: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent={{ xs: "flex-start", sm: "space-between" }}
        gap={1}
        flexWrap={{ xs: "wrap", sm: "nowrap" }}
        sx={{ minWidth: 0 }}
      >
        <TextField
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Search users"
          size="small"
          fullWidth
          sx={{
            minWidth: { xs: "100%", sm: 140 },
            maxWidth: { sm: 280, md: 260 },
            flex: 1
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
        {toolbarActions ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "flex-start", sm: "flex-end" },
              gap: 0.5,
              width: { xs: "100%", sm: "auto" },
              flexWrap: "wrap"
            }}
          >
            {toolbarActions}
          </Box>
        ) : null}
      </Stack>
      <Box sx={{ width: "100%", minWidth: 0, overflowX: "auto" }}>
        <DataGrid
          autoHeight
          density="compact"
          hideFooterSelectedRowCount
          checkboxSelection
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(model) => {
            let ids: string[] = [];
            if (model && typeof model === "object" && "ids" in model) {
              const idSet = (model as { ids?: Set<unknown> | Iterable<unknown> }).ids;
              const normalized = idSet instanceof Set ? Array.from(idSet) : Array.from(idSet ?? []);
              ids = normalized.map(String);
            } else if (Array.isArray(model)) {
              ids = (model as unknown[]).map(String);
            }
            onSelectionChange(ids);
          }}
          rows={rows}
          columns={columns}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 25 }
            }
          }}
          disableRowSelectionOnClick={false}
          sx={{
            minWidth: 420,
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              py: 0.75
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default UserTable;
